import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PhoneCall, Facebook,
  ArrowUp, ArrowDown, CalendarClock, Stethoscope, Check,
  Circle, BarChart3
} from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { todayBkk } from '../../lib/ticket.js'
import { nameInitial } from '../../lib/text.js'
import { useMe } from '../../lib/me.js'
import { useXanoRealtime } from '../../lib/realtime.js'
import { asList, toISO, addDays } from '../statistic/statShared.jsx'
import { STATUS_GROUPS, STATUS_TONES, statusTone } from '../../data/patientStatuses.js'
import { HIGH_BILLING_TAGS, REIMBURSEMENT_TAGS, tagMeta, asTags } from '../../data/recallTags.js'
import { lockState, LOCK_META } from '../../lib/recallLock.js'
import RecallPopup from '../../components/recall/RecallPopup.jsx'
import RecallNoteText from '../../components/recall/RecallNoteText.jsx'
import { noteLabel } from '../../data/implantOutcomes.js'
import {
  Page, PageHeader, Tabs, SubTabs, Segmented, FilterCard, Field, Input, SearchInput, Select,
  BranchSelect, useBranches, branchName as kitBranchName, DataTable, IconButton, PatientName,
  fmtDate, fmtTime, fmtNumber, fmtMoney, toISODate,
} from '../../kit'

/* -------------------------------------------------------------------------- */
/* Recall — the ติดตาม (follow-up / call-back) worklist. Each row is a patient we  */
/* should reach out to; `type` picks WHY they surface (a different Xano query per  */
/* category) and `recall_yet` splits already-called vs not-yet.                    */
/*                                                                                 */
/* TWO endpoints back this page (see buildRequest): the Recall log tab uses the     */
/* legacy GET /recall (api:Bh0CIPqU); every other tab uses GET /recall/patient      */
/* (api:FeVDPm_m), which queries the materialized high_billing[] / reimbursement[]  */
/* tag arrays. Both are server-paged. Row accessors below tolerate BOTH shapes      */
/* (recall-record rows vs patient rows).                                            */
/* -------------------------------------------------------------------------- */

/* The page is really TWO tools that happen to share a table, and they were reading
 * as one flat chip row. Split into two levels:
 *   level 1 — MODE: a worklist of patients to call  vs  the log of calls made
 *   level 2 — PRESET: which slice of the worklist (only shown in worklist mode)
 * `type` stays the single source of truth ('' = log), so buildRequest is unchanged. */
const LOG_TYPE = { key: '', label: 'Recall log', hint: 'บันทึกการติดตามตามวันที่' }
const PRESETS = [
  { key: 'Normal', label: 'Normal', hint: 'คนไข้ที่เคยรักษาแล้ว' },
  { key: 'Top Spender', label: 'Top Spender', hint: 'ยอดค่ารักษาสูง' },
  { key: 'High Billing', label: 'High Billing', hint: 'ตามประเภทการเรียกเก็บ' },
  { key: 'เบิกย้อนหลัง', label: 'เบิกย้อนหลัง', hint: 'มีรายการตามเบิก' },
  { key: 'Error', label: 'Error', hint: 'ข้อมูลไม่สมบูรณ์' },
]
const TYPES = [LOG_TYPE, ...PRESETS]

// recall_yet is a Xano bool: undefined = ทั้งหมด, false = ยังไม่ recall, true = recall แล้ว
const RECALL_YET = [
  { key: 'all', label: 'ทั้งหมด', v: undefined },
  { key: 'no', label: 'ยังไม่ recall', v: false },
  { key: 'yes', label: 'recall แล้ว', v: true },
]

// `key` is what the OLD /recall (Recall log tab) expects — Thai labels, historically.
// `col` is the real patient column, which is what /recall/patient's $sort_col wants.
const SORTS = [
  { key: 'วันRecall', label: 'วัน Recall', col: 'last_recall' },
  { key: 'ค่ารักษา', label: 'ค่ารักษา', col: 'total_price' },
  { key: 'วันนัด', label: 'วันนัด', col: 'last_appointment' },
  { key: 'วันรักษา', label: 'วันรักษา', col: 'last_treatment' },
]
const sortCol = (key) => SORTS.find((s) => s.key === key)?.col || 'last_recall'

// The status picker offers Recall statuses AND ประเภทเคส (ปกส / รากเทียม / Tooth Taxi)
// — a case type is just as valid a reason to call someone. Rendered as
// grouped options so the two sets stay visually distinct. ปิดเคส/ระบบ are excluded: the
// query filters ยกเลิกเคส/มีนัดแล้ว/Error out anyway (Error has its own preset).
const STATUS_PICKER = STATUS_GROUPS.filter((g) => g.label === 'Recall' || g.label === 'ประเภทเคส')
const STATUS_OPTIONS = [
  { value: '', label: 'ทุกสถานะ' },
  ...STATUS_PICKER.flatMap((g) => g.items.map((st) => ({ value: st, label: st, group: g.label }))),
]

const TOP_SPENDER_DEFAULT = '30000'
// how many log rows the ประวัติ Recall summary aggregates in one request
const STATS_CAP = 500

const HIGH_BILLING_VALUES = HIGH_BILLING_TAGS.map((t) => t.value)
const REIMBURSEMENT_VALUES = REIMBURSEMENT_TAGS.map((t) => t.value)

/* Build the request for a type tab. Two DIFFERENT endpoints back this page:
 *
 *  • '' (Recall log) → GET /api/recall (api:Bh0CIPqU) — the recall-log query,
 *    filtered by date. Takes the legacy Thai `sort_by` keys.
 *  • everything else → GET /api/recall/patient (api:FeVDPm_m) — the tag-driven
 *    patient worklist, keyed on the materialized tag arrays.
 *
 * BOTH are server-paged. The tag endpoint was briefly unpaged and paged in the
 * browser instead; at 34k patients that meant a multi-MB payload on every filter
 * change, so paging/search/branch/sort all moved upstream. Don't reintroduce
 * client-side filtering here — it would silently only filter the current page.
 *
 * Tag arrays use `overlaps?` upstream = OR. Omitting a tag param skips the filter
 * entirely rather than meaning "has any tag" — so for "ทุกประเภท/ทุกผู้จ่าย" we send
 * the FULL known tag list, which is what makes the tab mean "carries this theme".
 * Consequence: a tag the server knows but recallTags.js doesn't is invisible under
 * "ทุกประเภท". Keep that file in sync with the nightly recompute's stat sets.
 */
function buildRequest({ type, status, billingType, reimbursement, recallYetV, date, page, perPage, sortBy, orderBy, branchId, search, exceptDay, minTotal }) {
  if (type === '') {
    // ประวัติ Recall — a log of calls already made. `recall_yet` is meaningless here
    // (every row IS a recall) and so is except_recall_day, so neither is sent.
    // The upstream where uses `recall.date == $input.date` (NOT `==?`), so a missing
    // date matches nothing rather than meaning "all dates" — fall back to today so a
    // cleared box can't look like an empty day. Sort is fixed upstream
    // (recall.created_at desc), so sort_by/order_by are not sent either.
    return {
      path: '/api/recall',
      params: {
        date: date || todayBkk(),
        search: search || undefined,
        branch_id: branchId === '0' ? undefined : branchId,
        page, per_page: perPage,
      },
    }
  }
  const params = {
    page, per_page: perPage,
    // "ไม่ Recall ซ้ำภายใน N วัน" — we send the CUTOFF DATE, not N. Deriving it here
    // rather than upstream keeps the XanoScript free of the timestamp filter pipes
    // that threw ERROR_FATAL "Invalid pipe", and pins the day boundary to the
    // clinic's local timezone instead of the server's.
    exclude_recall_before: exceptDay ? toISO(addDays(new Date(), -Number(exceptDay))) : undefined,
    sort_by: sortCol(sortBy), order_by: orderBy,
    search: search || undefined,
    branch_id: branchId === '0' ? undefined : branchId,
    recall_yet: recallYetV === undefined ? undefined : String(recallYetV),
  }
  if (type === 'Normal') params.pt_status = status || undefined
  else if (type === 'Error') params.pt_status = 'Error'
  else if (type === 'High Billing') params.high_billing = billingType ? [billingType] : HIGH_BILLING_VALUES
  else if (type === 'เบิกย้อนหลัง') params.reimbursement = reimbursement ? [reimbursement] : REIMBURSEMENT_VALUES
  // Without a floor this is "every patient, sorted by spend" — 100k+ rows, which is
  // what made the preset hang. The threshold IS the preset. Sort isn't forced here:
  // selectPreset() already defaults it to ค่ารักษา/desc, and leaving the เรียงตาม
  // control live means it can't show one thing while the query does another.
  else if (type === 'Top Spender') params.min_total_price = minTotal || TOP_SPENDER_DEFAULT
  return { path: '/api/recall/patient', params }
}

/* ── tolerant field accessors ────────────────────────────────────────────────
 * Rows arrive in TWO shapes and every accessor must handle both:
 *   • /recall/patient → a patient row; fields sit at the top level.
 *   • /recall (log)   → a RECALL row joined to patient. Flat fields come from the
 *     query's `eval` (fullname/phone/HN/status/total_price/branch_id), and the rest
 *     hang off the `patient` addon (`r.patient.last_recall`, `.recall_count`,
 *     `.high_billing`, `.contact.*`), with last appt/treatment as `r.appointment` /
 *     `r.treatment` objects. Hence the `?? r.patient?.…` fallback on each line. */
// ⚠️ On a LOG row `r.id` is the RECALL id, not the patient — so `patient_id` (and
// the joined `patient.id`) must win before falling back to `r.id` (which is only
// the patient id on worklist rows).
const rPatientId = (r) => r.patient_id ?? r.patient?.id ?? r.id ?? null
const rFullname = (r) => String(r.fullname || [r.name, r.surname].filter(Boolean).join(' ') || '').trim()
const rHN = (r) => r.HN ?? r.hn ?? ''
const rStatus = (r) => r.status ?? ''
const rPhone = (r) => r.phone ?? r.contact?.phone ?? r.patient?.contact?.phone ?? ''
const rFacebook = (r) => r.facebook ?? r.contact?.facebook ?? r.patient?.contact?.facebook ?? ''
const rBranchId = (r) => { const b = r.branch_id ?? r.patient?.branch_id; return b != null ? String(b) : '' }
const rTotal = (r) => Number(r.total_price ?? r.patient?.total_price ?? 0) || 0
const rRecallCount = (r) => Number(r.recall_count ?? r.patient?.recall_count ?? (Array.isArray(r._recall) ? r._recall.length : 0)) || 0
const rApptCount = (r) => Number(r.appointment_count ?? 0) || 0
const rTxCount = (r) => Number(r.treatment_count ?? 0) || 0
const rHighBilling = (r) => asTags(r.high_billing ?? r.patient?.high_billing)
const rReimbursement = (r) => asTags(r.reimbursement ?? r.patient?.reimbursement)

/* log-only. `Note` is capital-N on the recall row — the whole point of a call log,
 * so it gets a column here even though the worklist deliberately has none. */
const rNote = (r) => String(r.Note ?? r.note?.detail ?? '').trim()
// `created_by` is what POST /recall actually writes; `creator` is absent from that
// stack's data map (silently dropped) and survives only on legacy rows.
const rSuccess = (r) => r.success === true
// the newest call attached to this recall, if the phone system logged one
const rCall = (r) => (Array.isArray(r._phone) && r._phone.length ? r._phone[0] : null)

// extract an ISO date from string | epoch-ms | { date/start_date/last_*/…_lastest_date }
// NB the log addons return { appointment_lastest_date: <epoch ms> } and
// { treatment_lastest_date: "YYYY-MM-DD" } — two different shapes, both handled here.
function dateOf(v) {
  if (v == null || v === '') return ''
  if (typeof v === 'object') return dateOf(v.appointment_lastest_date ?? v.treatment_lastest_date ?? v.date ?? v.start_date ?? v.last_treatment ?? v.last_appointment ?? v.created_at ?? '')
  // epoch → the BANGKOK calendar day (it used to be the UTC day, which put
  // anything before 07:00 on the previous date)
  if (typeof v === 'number') return toISODate(v)
  const s = String(v)
  if (/^\d{10,}$/.test(s)) return toISODate(Number(s))
  return s.slice(0, 10)
}
// on a log row `r.date` IS the recall date; on a patient row it's `last_recall`
const rLastRecall = (r) => dateOf(r.last_recall ?? r.patient?.last_recall ?? r.date)

/* ── log-only, RECALL-level ──────────────────────────────────────────────────
 * ⚠️ Do NOT use rLastRecall() in the log: it prefers `last_recall`, which comes
 * from the joined PATIENT (their most recent recall overall) — on a patient with
 * several recalls every row would print the same date. These read the recall
 * record's own columns. */
const rRecallDate = (r) => dateOf(r.date ?? r.created_at)
const rRecallTime = (r) => fmtTime(r?.created_at)
const rLastAppt = (r) => dateOf(r.last_appointment ?? r.appointment ?? r.patient?.last_appointment)
const rLastTx = (r) => dateOf(r.last_treatment ?? r.treatment ?? r.patient?.last_treatment)

/* -------------------------------------------------------------------------- */
export default function Recall() {
  // server filters (persisted)
  const [type, setType] = usePersistedState('recall:type', 'Normal')
  const [preset, setPreset] = usePersistedState('recall:preset', 'Normal')  // last worklist slice
  const [recallYet, setRecallYet] = usePersistedState('recall:recallYet', 'all')
  const [branchId, setBranchId] = usePersistedState('recall:branch', '0')
  const [sortBy, setSortBy] = usePersistedState('recall:sortBy', 'วันRecall')
  const [orderBy, setOrderBy] = usePersistedState('recall:orderBy', 'desc')
  const [exceptDay, setExceptDay] = usePersistedState('recall:excludeDay', '30')
  const [status, setStatus] = usePersistedState('recall:status', '')          // Normal
  const [billingType, setBillingType] = usePersistedState('recall:billingType', '') // High Billing
  const [reimbursement, setReimbursement] = usePersistedState('recall:reimbursement', '') // เบิกย้อนหลัง
  // ประวัติ Recall defaults to TODAY (Bangkok) on every open — deliberately not
  // persisted, so the log can never come up showing a stale day. '' = all dates.
  const [date, setDate] = useState(todayBkk())
  const [minTotal, setMinTotal] = usePersistedState('recall:minTotal', TOP_SPENDER_DEFAULT) // Top Spender
  // The two modes search different things (a patient worklist vs a day's call log),
  // so they keep SEPARATE search boxes — a name typed in one must not follow you
  // into the other and silently narrow it.
  const isLog = type === ''
  const [qWait, setQWait] = useState('')
  const [qLog, setQLog] = useState('')
  const q = isLog ? qLog : qWait
  const setQ = isLog ? setQLog : setQWait
  const [perPage] = useState(30)
  const [page, setPage] = useState(1)

  // Branch list for the สาขา column. A row saved under สนามเป้า (head office) reads
  // as อารีย์ — kit/branches.js.
  const branches = useBranches()

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pageTotal, setPageTotal] = useState(1)
  const [state, setState] = useState({ loading: true, err: '' })

  // ── lock + popup (mirrors the Call page) ──────────────────────────────────
  const me = useMe()
  const meId = me?.employee_id
  const [tick, setTick] = useState(0)                 // bump → refetch the list
  // Per-request result cache so toggling tabs / paging back is instant. Keyed by the full
  // request; a real refetch (after an action, or a realtime update) clears it so we never
  // show stale recall data. Filter/date changes get a new key → natural fresh fetch.
  const listCache = useRef(new Map())
  const statsCache = useRef(new Map())
  const refetch = () => { listCache.current.clear(); statsCache.current.clear(); setTick((t) => t + 1) }
  const [liveHint, setLiveHint] = useState(false)     // "มีอัปเดตใหม่" pulse
  const [selected, setSelected] = useState(null)      // the row whose popup is open
  const heldRef = useRef(null)                        // patient_id this session currently holds

  const lockCall = async (patient_id, request_type) => {
    // best-effort. ⚠️ NOT healed by an expiry — there isn't one (see recallLock.js);
    // a lock that fails to release stays lit until someone takes it over (รับต่อ).
    try { await postJson('/api/recall/lock', { patient_id, request_type }) } catch { /* takeover is the remedy */ }
  }
  const claim = async (p) => { await lockCall(p, 'claim'); heldRef.current = p }
  const release = async (p) => { if (p == null) return; await lockCall(p, 'release'); if (String(heldRef.current) === String(p)) heldRef.current = null }

  // Open a patient's recall popup. One patient at a time: release whoever I'm
  // currently holding before claiming the new one (the auto-release the user asked
  // for). Claiming broadcasts on `recall_update` so coworkers' lists update live.
  const openRow = async (r) => {
    const p = rPatientId(r)
    if (p == null) return
    // stamp the resolved patient_id so the popup can't re-resolve a LOG row's
    // `id` (the recall id) as the patient
    setSelected({ ...r, patient_id: p })
    // The read-only ประวัติ Recall log has no lock badges — claiming there only
    // broadcasts recall_update and bounces the whole table back at us. Just open it.
    if (isLog) return
    if (heldRef.current != null && String(heldRef.current) !== String(p)) await release(heldRef.current)
    await claim(p)
    refetch()
  }
  const closePopup = async () => {
    const p = selected ? rPatientId(selected) : null
    setSelected(null)
    if (isLog) return // nothing was claimed in the log view
    await release(p)
    refetch()
  }
  // claim/release from inside the popup (รับต่อ / ปล่อย)
  const popupLockAction = async (request_type) => {
    const p = selected ? rPatientId(selected) : null
    if (p == null) return
    if (request_type === 'claim') await claim(p); else await release(p)
    refetch()
  }
  // release the held lock if the page unmounts (best-effort; nothing backstops it —
  // there is no expiry window, so a missed release stays lit until a takeover)
  useEffect(() => () => { if (heldRef.current != null) lockCall(heldRef.current, 'release') }, [])

  // A patient was deleted from inside the drawer (only possible when they had no
  // นัดหมาย / การรักษา / รายการเรียกเก็บ — the proxy re-checks). Drop the row locally
  // instead of refetching so the worklist doesn't jump, and clear the caches so the
  // next real fetch can't serve the deleted patient back.
  const onPatientDeleted = (deletedId) => {
    if (String(heldRef.current) === String(deletedId)) heldRef.current = null
    setSelected(null)
    setRows((rs) => rs.filter((x) => String(rPatientId(x)) !== String(deletedId)))
    setTotal((t) => Math.max(0, t - 1))
    listCache.current.clear(); statsCache.current.clear()
  }

  // Realtime: refetch (debounced) when another station moves this worklist. TWO
  // sources now share `recall_update`:
  //   • POST /recall/lock — a claim/release. { type:"patient", employee_id,
  //     patient_id, lock, lock_by }. Carries an actor, so we skip our own echo.
  //   • the `patient_update - patient` table trigger (2026-09-09) — a patient's
  //     STATUS changed. { type:"patient", action:"status", patient_id, status }.
  //     Status decides worklist membership (see the filter note at the top), so a
  //     change made anywhere — this CRM, the doctor portal, Bubble, or nightly task
  //     7 flipping a lapsed มีนัดแล้ว to Error — has to move the list. Table 19 has
  //     no modified_by, so these carry NO employee_id and the writer refetches too.
  // ⚠️ Trigger-sourced events arrive as `payload.data`, endpoint-sourced ones as
  // `payload` — read both, or every status event looks like an empty object.
  // Only meaningful in worklist mode.
  const liveTimer = useRef(null)
  useXanoRealtime('recall_update', (msg) => {
    const p = msg?.payload?.data ?? msg?.payload
    const emp = p && typeof p === 'object' ? p.employee_id : null
    if (emp != null && meId != null && String(emp) === String(meId)) return
    setLiveHint(true)
    clearTimeout(liveTimer.current)
    liveTimer.current = setTimeout(refetch, 400)
  })
  useEffect(() => () => clearTimeout(liveTimer.current), [])

  // 30 วัน is a real default, not just an initial value: a blank box means "no
  // filter at all", which is never what we want on load. usePersistedState only
  // applies its initial when the key is ABSENT, so a stored "" (or a box the user
  // cleared last session) would silently disable the filter forever. Normalize once
  // on mount so a fresh profile and a stale one behave identically.
  useEffect(() => { if (!exceptDay) setExceptDay('30') }, [])

  // debounce the free-text search
  const [qDebounced, setQDebounced] = useState('')
  useEffect(() => { const t = setTimeout(() => setQDebounced(q.trim()), 350); return () => clearTimeout(t) }, [q])
  // switching mode swaps which box is live — apply it immediately rather than
  // letting the 350ms debounce fire one request carrying the other mode's text
  useEffect(() => { setQDebounced(q.trim()) }, [isLog])

  // reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1) }, [type, recallYet, branchId, sortBy, orderBy, exceptDay, status, billingType, reimbursement, date, qDebounced])

  const recallYetV = RECALL_YET.find((x) => x.key === recallYet)?.v
  const req = buildRequest({
    type, status, billingType, reimbursement, recallYetV, date,
    page, perPage, sortBy, orderBy, branchId, search: qDebounced, exceptDay, minTotal,
  })
  const reqKey = JSON.stringify([req.path, req.params])

  // Switching between the worklist and the log swaps the row SHAPE (patient rows vs
  // recall rows), so the old rows are dropped rather than dimmed under the other
  // mode's columns. Declared before the fetch so a cache hit still lands after it.
  useEffect(() => { setRows([]) }, [isLog])

  const reqId = useRef(0)
  useEffect(() => {
    const id = ++reqId.current // bump always, so any in-flight fetch is ignored on a cache hit too
    const cached = listCache.current.get(reqKey)
    if (cached) {
      setRows(cached.rows); setTotal(cached.total); setPageTotal(cached.pageTotal)
      setState({ loading: false, err: '' })
      return
    }
    setState({ loading: true, err: '' })
    apiGet(req.path, req.params)
      .then((r) => {
        if (id !== reqId.current) return
        const list = asList(r)
        const total = Number(r?.itemsTotal ?? r?.itemsReceived ?? list.length) || 0
        const pageTotal = Number(r?.pageTotal ?? 1) || 1
        listCache.current.set(reqKey, { rows: list, total, pageTotal })
        setRows(list); setTotal(total); setPageTotal(pageTotal)
        setState({ loading: false, err: '' })
        setLiveHint(false) // the realtime change is now on screen — drop the pulse (as CallList does)
      })
      .catch((e) => { if (id === reqId.current) setState({ loading: false, err: String(e.message || e) }) })
  }, [reqKey, tick])

  /* ── ประวัติ Recall — day summary ──────────────────────────────────────────
   * The table is server-paged (30/row), so aggregating `rows` would answer "how
   * many calls today?" with ONE page's worth. Pull the whole filtered set in a
   * single capped request and count that instead. Keyed WITHOUT page/per_page so
   * paging the table doesn't refetch the summary. If the day exceeds the cap the
   * subtitle says so rather than quietly under-reporting. */
  const [logStats, setLogStats] = useState(null)
  const statsId = useRef(0)
  const statsKey = JSON.stringify([req.path, { ...req.params, page: undefined, per_page: undefined }])
  useEffect(() => {
    if (!isLog) { setLogStats(null); return }
    const id = ++statsId.current
    const cached = statsCache.current.get(statsKey)
    if (cached) { setLogStats(cached); return }
    setLogStats(null)
    apiGet(req.path, { ...req.params, page: 1, per_page: STATS_CAP })
      .then((r) => {
        if (id !== statsId.current) return
        const list = asList(r)
        const s = { total: list.length, answered: 0, noAnswer: 0, unknown: 0, success: 0, itemsTotal: Number(r?.itemsTotal ?? list.length) || list.length }
        for (const row of list) {
          if (rSuccess(row)) s.success++
          const c = rCall(row)
          if (!c) { s.unknown++; continue }        // recall logged with no phone record linked
          if (c.call_result === 'answered') s.answered++
          else s.noAnswer++
        }
        statsCache.current.set(statsKey, s)
        setLogStats(s)
      })
      .catch(() => { if (id === statsId.current) setLogStats(null) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsKey, tick, isLog])

  // Switching back to the worklist restores the preset you were last on, so the
  // mode toggle is non-destructive — it never silently resets you to Normal.
  function selectPreset(key) {
    setPreset(key); setType(key)
    // Top Spender has no server-side filter — the preset IS the sort.
    if (key === 'Top Spender') { setSortBy('ค่ารักษา'); setOrderBy('desc') }
  }

  // ประวัติ Recall: collapse the flat recall-record list into one entry per patient,
  // keeping every recall. Server order (created_at desc) is preserved — a Map keeps
  // first-seen insertion order, so the newest recall still leads the page.
  const logGroups = useMemo(() => {
    if (!isLog) return []
    const map = new Map()
    for (const r of rows) {
      const p = rPatientId(r)
      const key = String(p ?? `r${r.id}`)
      let g = map.get(key)
      if (!g) { g = { key, pid: p, patient: r, recalls: [] }; map.set(key, g) }
      g.recalls.push(r)
    }
    return [...map.values()]
  }, [rows, isLog])

  const branchName = (bid) => kitBranchName(bid, branches)

  function resetFilters() {
    // stay in the current mode — clearing filters shouldn't teleport you to the log
    setType(isLog ? '' : 'Normal'); setPreset('Normal')
    setRecallYet('all'); setBranchId('0'); setSortBy('วันRecall'); setOrderBy('desc')
    setExceptDay('30'); setStatus(''); setBillingType(''); setReimbursement('')
    setMinTotal(TOP_SPENDER_DEFAULT); setDate(todayBkk()); setQWait(''); setQLog('')
  }
  // Exactly what resetFilters() would put back — drives ล้างตัวกรอง's enabled state
  // and the "no match" (vs "nothing here") empty message.
  const filtersDefault = (isLog || type === 'Normal')
    && recallYet === 'all' && branchId === '0' && sortBy === 'วันRecall' && orderBy === 'desc'
    && String(exceptDay) === '30' && !status && !billingType && !reimbursement
    && String(minTotal) === TOP_SPENDER_DEFAULT && date === todayBkk() && !qWait && !qLog

  const hint = TYPES.find((t) => t.key === type)?.hint
  const worklistColumns = worklistColumnsFor({ meId, branchName })
  const logColumns = LOG_COLUMNS
  const logRows = useMemo(() => logGroups.flatMap((g) => g.recalls.map((r, idx) => ({ r, g, idx }))), [logGroups])
  const isSel = (r) => selected != null && String(rPatientId(selected)) === String(rPatientId(r))

  return (
    <Page width="wide">
      <PageHeader
        icon={PhoneCall}
        breadcrumb={[{ label: 'ติดตาม', to: '/follow-ups' }, { label: 'Recall' }]}
        title={<span className="inline-flex items-center gap-2">Recall <LiveDot on={liveHint} /></span>}
        subtitle="รายชื่อคนไข้ที่ต้องติดตาม/โทรกลับ · เลือกประเภทเพื่อดูกลุ่มคนไข้แต่ละแบบ"
      >
        {/* level 1 — which tool: a worklist of patients to call vs the log of calls
            made. Switching back to the worklist restores the last preset. */}
        <Tabs
          items={[{ key: 'wait', label: 'คนไข้รอ Recall' }, { key: 'log', label: 'ประวัติ Recall' }]}
          value={isLog ? 'log' : 'wait'}
          onChange={(k) => { if (k === 'log') setType(''); else if (isLog) selectPreset(preset) }}
        />
        {/* level 2 — which slice of the worklist */}
        {!isLog && (
          <SubTabs
            className="mt-3"
            items={PRESETS.map((t) => ({ key: t.key, label: t.label, title: t.hint }))}
            value={type}
            onChange={selectPreset}
          />
        )}
      </PageHeader>

      {/* filters */}
      <FilterCard onClear={resetFilters} canClear={!filtersDefault}
        aside={hint && <span className="hidden sm:inline text-[12px] text-slate-400">{hint}</span>}>
        <Field label="ค้นหา" className="flex-1 min-w-[220px]">
          <SearchInput value={q} onChange={setQ} placeholder="ชื่อ · HN · เบอร์โทร…" />
        </Field>

        {/* The API takes ONE branch_id, so อารีย์ here does not also match old
            สนามเป้า rows (kit rule: branchFilterIds) — see the migration report. */}
        <Field label="สาขา" className="min-w-[160px]">
          <BranchSelect value={branchId === '0' ? '' : branchId} onChange={(v) => setBranchId(v === '' || v == null ? '0' : String(v))} />
        </Field>

        {/* Both of these are worklist-only: in ประวัติ Recall every row IS a recall,
            so "already recalled?" and "don't re-recall within N days" mean nothing. */}
        {!isLog && (
          <>
            <Field label="สถานะการ recall">
              <Segmented options={RECALL_YET.map((x) => ({ value: x.key, label: x.label }))} value={recallYet} onChange={setRecallYet} />
            </Field>

            <Field label="ไม่ Recall ซ้ำภายใน" hint={recallYet === 'no' ? 'ใช้ไม่ได้กับ “ยังไม่ recall”' : undefined}>
              {({ id }) => (
                <div className="inline-flex items-center gap-1.5">
                  <Input id={id} type="number" min="0" value={exceptDay} onChange={(e) => setExceptDay(e.target.value)} placeholder="—"
                    className="!w-[84px]" disabled={recallYet === 'no'} />
                  <span className="text-[12.5px] text-slate-500">วัน</span>
                </div>
              )}
            </Field>
          </>
        )}

        {/* type-conditional filters */}
        {type === 'Normal' && (
          <Field label="สถานะคนไข้" className="min-w-[190px]">
            <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} placeholder="ทุกสถานะ" />
          </Field>
        )}
        {type === 'Top Spender' && (
          <Field label="ค่ารักษาตั้งแต่">
            {({ id }) => (
              <div className="inline-flex items-center gap-1.5">
                <Input id={id} type="number" min="0" step="1000" value={minTotal} onChange={(e) => setMinTotal(e.target.value)}
                  placeholder={TOP_SPENDER_DEFAULT} className="!w-[120px] tabular-nums" />
                <span className="text-[12.5px] text-slate-500">บาทขึ้นไป</span>
              </div>
            )}
          </Field>
        )}
        {/* Both tag filters send ONE tag; Xano matches it inside the patient's
            tag array. Empty = any tag (i.e. every patient carrying the theme). */}
        {type === 'High Billing' && (
          <Field label="ประเภทการเรียกเก็บ" className="min-w-[170px]">
            <Select value={billingType} onChange={setBillingType} placeholder="ทุกประเภท"
              options={[{ value: '', label: 'ทุกประเภท' }, ...HIGH_BILLING_TAGS.map((t) => ({ value: t.value, label: t.label }))]} />
          </Field>
        )}
        {type === 'เบิกย้อนหลัง' && (
          <Field label="ผู้จ่าย / เบิก" className="min-w-[180px]">
            <Select value={reimbursement} onChange={setReimbursement} placeholder="ทุกผู้จ่าย"
              options={[{ value: '', label: 'ทุกผู้จ่าย' }, ...REIMBURSEMENT_TAGS.map((t) => ({ value: t.value, label: t.label }))]} />
          </Field>
        )}
        {isLog && (
          <Field label="วันที่ (recall log)">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="!w-[170px]" />
          </Field>
        )}

        {/* The log query sorts by recall.created_at desc upstream and ignores
            sort_by/order_by, so the control is worklist-only rather than a
            dropdown that visibly does nothing. The column headers sort too —
            both drive the same server-side sort. */}
        {!isLog && (
          <Field label="เรียงตาม">
            {({ id }) => (
              <div className="inline-flex items-center gap-1.5">
                <div className="min-w-[130px]">
                  <Select id={id} value={sortBy} onChange={setSortBy} options={SORTS.map((x) => ({ value: x.key, label: x.label }))} />
                </div>
                <IconButton variant="secondary" icon={orderBy === 'desc' ? ArrowDown : ArrowUp}
                  label={orderBy === 'desc' ? 'มาก→น้อย' : 'น้อย→มาก'}
                  onClick={() => setOrderBy((o) => (o === 'desc' ? 'asc' : 'desc'))} />
              </div>
            )}
          </Field>
        )}
      </FilterCard>

      {/* ประวัติ Recall summary — calls made / answered / success */}
      {isLog && logStats && <LogStats stats={logStats} />}

      {/* table — a refresh (after a claim/release, a note, or a realtime update)
          keeps the rows on screen dimmed; only a first load shows the spinner. */}
      {isLog ? (
        <DataTable
          columns={logColumns}
          rows={logRows}
          rowKey={(x) => x.r.id ?? `${x.g.key}-${x.idx}`}
          onRowClick={(x) => openRow(x.r)}
          rowClassName={(x) => (isSel(x.r) ? 'bg-brand-50/60' : rSuccess(x.r) ? 'bg-emerald-50/40' : '')}
          groupBy={(x) => x.g.key}
          groupHeader={(k, groupRows) => {
            const x = groupRows[0]
            return <LogGroupHeader g={x.g} meId={meId} branchName={branchName} onOpen={() => openRow(x.r)} />
          }}
          fixed
          minWidth="760px"
          loading={state.loading}
          error={state.err || undefined}
          onRetry={refetch}
          filtered={!filtersDefault}
          onClearFilters={resetFilters}
          empty={{ title: 'ยังไม่มีบันทึก Recall ในวันที่เลือก' }}
          page={page}
          pageTotal={pageTotal}
          onPageChange={setPage}
          total={total}
        />
      ) : (
        <DataTable
          columns={worklistColumns}
          rows={rows}
          rowKey={(r, i) => (r.id ?? rPatientId(r) ?? 'row') + '-' + i}
          onRowClick={openRow}
          rowClassName={(r) => (isSel(r) ? 'bg-brand-50/60' : '')}
          sort={{ key: sortBy, dir: orderBy }}
          onSortChange={({ key, dir }) => { setSortBy(key); setOrderBy(dir) }}
          fixed
          minWidth="1280px"
          loading={state.loading}
          error={state.err || undefined}
          onRetry={refetch}
          filtered={!filtersDefault}
          onClearFilters={resetFilters}
          empty={{ title: 'ไม่พบคนไข้ในกลุ่มนี้' }}
          page={page}
          pageTotal={pageTotal}
          onPageChange={setPage}
          total={total}
        />
      )}

      {/* recall popup — opens on a row click, holds the lock while open */}
      {selected && (
        <RecallPopup
          key={rPatientId(selected)}
          patient={selected}
          me={me}
          onClose={closePopup}
          onLockAction={popupLockAction}
          onChanged={refetch}
          onDeleted={onPatientDeleted}
          refreshKey={tick}
        />
      )}
    </Page>
  )
}

/* ── worklist columns ─────────────────────────────────────────────────────────
 * The four date/money columns sort on the SERVER (their keys are the `sortBy`
 * values buildRequest sends), the same sort the เรียงตาม control drives. */
function worklistColumnsFor({ meId, branchName }) {
  return [
    {
      key: 'patient', label: 'คนไข้',
      render: (r) => <PatientCell row={r} lock={lockState(r, meId)} />,
    },
    {
      key: 'contact', label: 'ติดต่อ', width: '140px',
      render: (r) => <ContactCell row={r} />,
    },
    { key: 'branch', label: 'สาขา', width: '104px', className: 'truncate', render: (r) => <span className="text-slate-500">{branchName(rBranchId(r))}</span> },
    { key: 'hb', label: 'High Billing', width: '140px', render: (r) => <TagCell tags={rHighBilling(r)} title="High Billing" /> },
    { key: 'rb', label: 'ตามเบิก', width: '140px', render: (r) => <TagCell tags={rReimbursement(r)} title="ตามเบิก" /> },
    {
      key: 'วันRecall', label: 'Recall ล่าสุด', width: '128px', sortable: true, defaultDir: 'desc',
      render: (r) => {
        const rc = rRecallCount(r)
        return (
          <>
            <span className="text-slate-600 tabular-nums">{rLastRecall(r) ? fmtDate(rLastRecall(r)) : <span className="text-slate-400">ยังไม่เคย</span>}</span>
            {rc > 0 && <span className="ml-1.5 inline-flex items-center rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[10.5px]">×{rc}</span>}
          </>
        )
      },
    },
    { key: 'วันนัด', label: 'นัดล่าสุด', width: '124px', sortable: true, defaultDir: 'desc', render: (r) => <CountDate icon={CalendarClock} date={rLastAppt(r)} count={rApptCount(r)} /> },
    { key: 'วันรักษา', label: 'รักษาล่าสุด', width: '124px', sortable: true, defaultDir: 'desc', render: (r) => <CountDate icon={Stethoscope} date={rLastTx(r)} count={rTxCount(r)} /> },
    { key: 'ค่ารักษา', label: 'ค่ารักษา', width: '104px', align: 'right', sortable: true, defaultDir: 'desc', render: (r) => (rTotal(r) ? fmtMoney(rTotal(r)) : '—') },
  ]
}

/* ── ประวัติ Recall columns ───────────────────────────────────────────────────
 * A log row IS a recall record, so the columns are RECALL-level: วันที่ · โน้ต ·
 * ผลโทร · สำเร็จ. The patient is stated ONCE, in the group heading above their
 * recalls (LogGroupHeader), so the recalls read as a sequence. The patient-level
 * columns the worklist uses (นัดล่าสุด / รักษาล่าสุด / ค่ารักษา) are deliberately
 * absent — repeating a patient's totals on every one of their recalls made the
 * log read like a patient list instead of a call log. */
const LOG_COLUMNS = [
  {
    key: 'date', label: 'วันที่ Recall', width: '132px',
    render: ({ r }) => (
      <>
        <span className="text-slate-600 tabular-nums">{rRecallDate(r) ? fmtDate(rRecallDate(r)) : '—'}</span>
        {rRecallTime(r) && <span className="block text-[11px] text-slate-400 tabular-nums">{rRecallTime(r)}</span>}
      </>
    ),
  },
  {
    key: 'note', label: 'โน้ต',
    render: ({ r }) => (rNote(r)
      ? <span className="block text-slate-700 truncate" title={noteLabel(rNote(r))}><RecallNoteText note={rNote(r)} title={noteLabel(rNote(r))} /></span>
      : <span className="text-slate-300">—</span>),
  },
  { key: 'call', label: 'ผลโทร', width: '160px', render: ({ r }) => <CallCell call={rCall(r)} /> },
  { key: 'success', label: 'สำเร็จ', width: '100px', render: ({ r }) => <SuccessCell success={rSuccess(r)} /> },
]

// The patient heading of one group in ประวัติ Recall: who, how to reach them,
// which branch, and how many recalls sit under it. Clicking it (anywhere but the
// name / HN link) opens the popup, like clicking one of their recalls.
function LogGroupHeader({ g, meId, branchName, onOpen }) {
  const head = g.patient          // any row in the group carries the patient join
  return (
    <div className="flex items-center gap-x-5 gap-y-1 flex-wrap font-normal cursor-pointer" onClick={onOpen}>
      <div className="min-w-[260px] flex-1"><PatientCell row={head} n={g.recalls.length} lock={lockState(head, meId)} /></div>
      <ContactCell row={head} />
      <span className="text-slate-500 text-[12.5px]">{branchName(rBranchId(head))}</span>
    </div>
  )
}

/* ── ประวัติ Recall summary ───────────────────────────────────────────────────
 * Answers the two questions asked of a day's calling: how many calls did we make
 * (and did anyone pick up), and how many turned into an appointment.
 *
 * `รับสาย + ไม่รับสาย` deliberately does NOT have to equal `โทรทั้งหมด`: a recall
 * can be logged with no phone record attached, and those are shown as their own
 * "ไม่มีบันทึกสาย" slice rather than being silently folded into ไม่รับสาย — which
 * would overstate the failure rate. */
function LogStats({ stats }) {
  const { total, answered, noAnswer, unknown, success, itemsTotal } = stats
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0)
  const seg = [
    { key: 'a', n: answered, cls: 'bg-emerald-500', label: 'รับสาย' },
    { key: 'n', n: noAnswer, cls: 'bg-rose-400', label: 'ไม่รับสาย' },
    { key: 'u', n: unknown, cls: 'bg-slate-300', label: 'ไม่มีบันทึกสาย' },
  ].filter((s) => s.n > 0)
  return (
    <section className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3.5">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={16} className="text-slate-400" />
        <h3 className="text-[13.5px] font-semibold text-slate-800">สรุปผล Recall</h3>
        <span className="text-[12.5px] text-slate-400">
          {itemsTotal > total
            ? `นับจาก ${fmtNumber(total)} รายการแรก (ทั้งหมด ${fmtNumber(itemsTotal)})`
            : `ทั้งหมด ${fmtNumber(total)} รายการ`}
        </span>
      </div>
        <div className="flex flex-wrap items-stretch gap-3">
          <StatTile label="โทรทั้งหมด" value={fmtNumber(total)} />
          <StatTile label="รับสาย" value={fmtNumber(answered)} hint={`${pct(answered)}%`} tone="emerald" />
          <StatTile label="ไม่รับสาย" value={fmtNumber(noAnswer)} hint={`${pct(noAnswer)}%`} tone="rose" />
          <StatTile label="สำเร็จ (เกิดนัด)" value={fmtNumber(success)} hint={`${pct(success)}%`} tone="brand" />
        </div>
        {seg.length > 0 && (
          <div className="mt-3">
            <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
              {seg.map((s) => (
                <div key={s.key} className={s.cls} style={{ width: `${(s.n / total) * 100}%` }} title={`${s.label} ${fmtNumber(s.n)}`} />
              ))}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-slate-500">
              {seg.map((s) => (
                <span key={s.key} className="inline-flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${s.cls}`} />{s.label} {fmtNumber(s.n)}
                </span>
              ))}
            </div>
          </div>
        )}
    </section>
  )
}
function StatTile({ label, value, hint, tone = 'slate' }) {
  const tones = { slate: 'text-slate-800', emerald: 'text-emerald-600', rose: 'text-rose-600', brand: 'text-brand-600' }
  return (
    <div className="flex-1 min-w-[130px] rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2">
      <div className="text-[11.5px] text-slate-500">{label}</div>
      <div className={`text-[20px] font-semibold tabular-nums leading-tight ${tones[tone]}`}>
        {value}{hint && <span className="ml-1.5 text-[12px] font-normal text-slate-400">{hint}</span>}
      </div>
    </div>
  )
}

/* ── presentational ─────────────────────────────────────────────────────────── */
// Lock indicator in the worklist row — who (if anyone) is calling this patient now.
function LockChip({ lock }) {
  const m = LOCK_META[lock.status] || LOCK_META.free
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium border whitespace-nowrap shrink-0 ${m.cls}`}>
      <Circle size={6} className={`${m.dot.replace('bg-', 'text-')} fill-current`} />{m.label}
    </span>
  )
}
// Patient-status pill — `whitespace-nowrap` so "Recall 6 เดือน" can't break across
// two lines and double the row height.
function StatusPill({ status, tone }) {
  if (!status) return null
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] ring-1 whitespace-nowrap shrink-0 ${tone.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tone.dot}`} />{status}
    </span>
  )
}
/* Patient identity block shared by both tables. The name and HN are the
 * patient's page (a real link, new tab — decision #4); the rest of the row opens
 * the recall popup. Tag chips sit on one fixed line below (flex, no wrap). */
function PatientCell({ row, n, lock }) {
  const st = rStatus(row)
  const tone = STATUS_TONES[statusTone(st)] || STATUS_TONES.none
  return (
    <span className="flex items-center gap-2 min-w-0 w-full">
      <span className="w-7 h-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[12px] font-semibold shrink-0">{nameInitial(rFullname(row) || '?')}</span>
      <span className="min-w-0">
        <span className="block truncate">
          <PatientName inline context="followup" patient={{ id: rPatientId(row), name: rFullname(row), HN: rHN(row) }} />
        </span>
        <span className="flex items-center gap-1 mt-0.5 overflow-hidden">
          <StatusPill status={st} tone={tone} />
          {n > 1 && <span className="inline-flex items-center rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[10.5px] whitespace-nowrap shrink-0">{n} ครั้ง</span>}
          {lock.status !== 'free' && <LockChip lock={lock} />}
        </span>
      </span>
    </span>
  )
}
// Phone + Facebook. The Facebook icon is its own link, so it must not also open the popup.
function ContactCell({ row }) {
  const fb = rFacebook(row)
  return (
    <span className="whitespace-nowrap">
      <span className="text-slate-600 tabular-nums text-[12.5px]">{rPhone(row) || '—'}</span>
      {fb ? <a href={/^https?:/.test(fb) ? fb : undefined} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} className="inline-flex ml-1.5 text-blue-500 align-middle" title={fb}><Facebook size={13} /></a> : null}
    </span>
  )
}
function LiveDot({ on }) {
  return (
    <span className="relative inline-flex h-2 w-2" title="อัปเดตเรียลไทม์">
      {on && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
      <span className={`relative inline-flex rounded-full h-2 w-2 ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} />
    </span>
  )
}
// high_billing[] / reimbursement[] tags — these say WHY the patient surfaced, so
// each array gets its own column rather than crowding the name. Unknown tags (ones
// the server has but recallTags.js doesn't list) still render, in slate; past `max`
// the rest collapse to "+N" with the full list in the tooltip, so a tall row can't
// push the whole table out of rhythm.
function TagCell({ tags, title, max = 2 }) {
  if (!tags?.length) return <span className="text-slate-300">—</span>
  const shown = tags.slice(0, max)
  const rest = tags.length - shown.length
  return (
    <span className="flex flex-wrap items-center gap-1" title={`${title}: ${tags.join(' · ')}`}>
      {shown.map((t) => {
        const m = tagMeta(t)
        const tone = STATUS_TONES[m.tone] || STATUS_TONES.slate
        return (
          <span key={t} className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] ring-1 ${tone.pill}`}>{m.label}</span>
        )
      })}
      {rest > 0 && <span className="inline-flex items-center rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[10.5px]">+{rest}</span>}
    </span>
  )
}
// Outcome of the logged call. `success` is the staff's own verdict on the recall;
// the _phone record is what the phone system observed (answered / cancelled + how
// long). They can disagree — a call can connect and still not book anything — so
// both are shown rather than collapsing them into one badge.
const CALL_RESULT = {
  answered: { label: 'รับสาย', tone: 'green' },
  cancelled: { label: 'ไม่รับสาย', tone: 'slate' },
  busy: { label: 'สายไม่ว่าง', tone: 'yellow' },
  failed: { label: 'ต่อไม่ติด', tone: 'orange' },
}
function CallCell({ call }) {
  if (!call) return <span className="text-slate-300">—</span>
  const meta = CALL_RESULT[call.call_result] || { label: call.call_result || '—', tone: 'slate' }
  const tone = STATUS_TONES[meta.tone] || STATUS_TONES.none
  const answered = call.call_result === 'answered'
  // On a call nobody picked up, talk time is 0 and meaningless — the number that
  // says something is how long it RANG before we gave up. So: answered → duration,
  // otherwise → ring_time, labelled so the two can't be mistaken for each other.
  const secs = Number((answered ? call.call_duration : call.ring_time) || 0)
  return (
    <span className="flex items-center gap-1 whitespace-nowrap">
      <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10.5px] ring-1 whitespace-nowrap shrink-0 ${tone.pill}`}>{meta.label}</span>
      {secs > 0 && <span className="text-[11px] text-slate-400 tabular-nums">{answered ? `${secs}s` : `ริง ${secs}s`}</span>}
    </span>
  )
}
// สำเร็จ is its own column now — a recall "succeeded" when it produced an
// appointment, which is a different fact from whether the phone was answered.
function SuccessCell({ success }) {
  if (!success) return <span className="text-slate-300">—</span>
  return (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium ring-1 whitespace-nowrap bg-emerald-50 text-emerald-700 ring-emerald-200">
      <Check size={10} strokeWidth={3} />สำเร็จ
    </span>
  )
}
function CountDate({ icon: Icon, date, count }) {
  if (!date && !count) return <span className="text-slate-300">—</span>
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-slate-600 tabular-nums">{date ? fmtDate(date) : '—'}</span>
      {count > 0 && <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[10.5px]"><Icon size={10} />{count}</span>}
    </span>
  )
}
